import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import { CancelToken } from "@/lib/utility/cancelToken";
import { MrfiktivPageMetaViewModelGen, MrfiktivPageViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { ThgPageMetaViewModelGen, ThgPageViewModelGen } from "@/services/thg/v1/data-contracts";
import { Action, Mutation, VuexModule } from "vuex-module-decorators";

export interface IPaginationParams {
  currentPage?: number;
  itemsPerPage?: number;
  order?: PageOrderEnum | "descending" | "ascending";
  startId?: string;
}

/**
 * Base class that can be extended to create stores to paginate data.
 *
 * @template DocumentType The type of the document that is listed in the Pagination
 * @template PaginationParamsType The parameters that are needed to make a pagination request
 */
export abstract class BasePagination<DocumentType, PaginationParamsType extends IPaginationParams> extends VuexModule {
  protected abstract _paginationList: DocumentType[];
  protected abstract _currentPage: number;
  protected abstract _itemsPerPage: number;
  protected abstract _totalItems: number;
  protected abstract _totalPages: number;
  protected abstract _pageOrder: PageOrderEnum;
  protected abstract _isLoadAll: boolean;
  cancelToken: CancelToken | undefined;

  protected async loadDocuments(
    query: PaginationParamsType
  ): Promise<
    (ThgPageViewModelGen | MrfiktivPageViewModelGen) & {
      data?: DocumentType[] | undefined;
    }
  > {
    throw new Error(`loadDocuments not implemented by parent class. Cannot process query ${query}`);
  }

  get paginationList(): DocumentType[] {
    return this._paginationList;
  }
  get currentPage(): number {
    return this._currentPage;
  }
  get itemsPerPage(): number {
    return this._itemsPerPage;
  }
  get totalItems(): number {
    return this._totalItems;
  }
  get totalPages(): number {
    return this._totalPages;
  }
  get pageOrder(): PageOrderEnum {
    return this._pageOrder;
  }
  get isLoadAll() {
    return this._isLoadAll;
  }

  get isComplete() {
    return this.paginationList.length >= this.totalItems;
  }
  get lastId() {
    if (this.paginationList.length === 0) {
      return undefined;
    }
    const lastDocument = this.paginationList[this.paginationList.length - 1] as any;
    const lastId = lastDocument._id || lastDocument.id;

    return lastId;
  }
  get lastIdIndex() {
    const lastId = this.lastId;
    if (!lastId) {
      return -1;
    }

    const lastIdIndex = this._paginationList.findIndex(el => {
      const id = (el as any)._id || (el as any).id;
      return id === lastId;
    });

    return lastIdIndex;
  }

  @Mutation
  private _mutatePaginationList(paginationList: DocumentType[]) {
    this._paginationList = paginationList;
  }
  @Mutation
  private _mutateCurrentPage(currentPage: number) {
    this._currentPage = currentPage;
  }
  @Mutation
  private _mutateItemsPerPage(itemsPerPage: number) {
    this._itemsPerPage = itemsPerPage;
  }
  @Mutation
  private _mutateTotalItems(totalItems: number) {
    this._totalItems = totalItems;
  }
  @Mutation
  private _mutateTotalPages(totalPages: number) {
    this._totalPages = totalPages;
  }
  @Mutation
  private _mutatePageOrder(pageOrder: PageOrderEnum) {
    this._pageOrder = pageOrder;
  }
  @Mutation
  private _mutateIsLoadAll(isLoadAll: boolean) {
    this._isLoadAll = isLoadAll;
  }
  @Mutation _mutateCancelToken(cancelToken: CancelToken) {
    this.cancelToken = cancelToken;
  }

  @Action
  setPaginationList(paginationList: DocumentType[]): void {
    this.context.commit("_mutatePaginationList", paginationList);
  }
  @Action
  setCurrentPage(currentPage: number): void {
    this.context.commit("_mutateCurrentPage", currentPage);
  }
  @Action
  setItemsPerPage(itemsPerPage?: number) {
    this.context.commit("_mutateItemsPerPage", itemsPerPage);
  }
  @Action
  setTotalItems(totalItems: number): void {
    this.context.commit("_mutateTotalItems", totalItems);
  }
  @Action
  setTotalPages(totalPages: number): void {
    this.context.commit("_mutateTotalPages", totalPages);
  }
  @Action
  setPageOrder(pageOrder: PageOrderEnum) {
    this.context.commit("_mutatePageOrder", pageOrder);
  }
  @Action
  setIsLoadAll(isLoadAll: boolean) {
    this.context.commit("_mutateIsLoadAll", isLoadAll);
  }
  @Action
  setCancelToken(cancelToken: CancelToken) {
    this.context.commit("_mutateCancelToken", cancelToken);
  }

  @Action
  replaceInList(document: DocumentType) {
    const paginationList = this._paginationList;

    const index = this._paginationList.findIndex((el: any) => {
      if (el.id) {
        return el.id === (document as any).id;
      } else {
        return (el as any)._id === (document as any)._id;
      }
    });

    if (index >= 0) {
      paginationList.splice(index, 1, document);
      this.context.commit("_mutatePaginationList", paginationList);
    }
  }
  @Action
  setToList(documents: DocumentType[]) {
    const paginationList = this.paginationList;
    if (!this.lastId) {
      this.context.dispatch("setPaginationList", documents);
    } else {
      paginationList.splice(this.lastIdIndex + 1, paginationList.length, ...(documents || []));
      this.context.dispatch("setPaginationList", paginationList);
    }
  }

  @Action
  setMeta(value: ThgPageMetaViewModelGen | MrfiktivPageMetaViewModelGen) {
    this.context.dispatch("setItemsPerPage", value.itemsPerPage);
    this.context.dispatch("setTotalItems", value.totalItems);
    this.context.dispatch("setTotalPages", value.totalPages);
    this.context.dispatch("setCurrentPage", value.currentPage);
  }

  /**
   * Loads the first page of items
   *
   * @param query
   * @returns
   */
  @Action
  async fetchFirst(query: PaginationParamsType): Promise<DocumentType[]> {
    const documents = await this.context.dispatch("loadDocuments", {
      itemsPerPage: this.itemsPerPage,
      order: this.pageOrder,
      ...query,
      currentPage: 1,
      startId: undefined
    });

    // set meta information
    this.context.dispatch("setMeta", documents.meta);

    // set items
    this.context.dispatch("setPaginationList", documents.data);

    return documents.data;
  }

  @Action
  async fetchAllFromBeginning(query: PaginationParamsType) {
    // cancel active request
    this.cancelToken?.requestCancellation();

    // create new cancel token for next series of requests
    const cancelToken = new CancelToken();

    // replace active cancel token
    this.context.dispatch("setCancelToken", cancelToken);

    // request first page
    const docs: DocumentType[] = await this.context.dispatch("fetchFirst", query);

    // check if all are supposed to be loaded
    if (!this.isLoadAll) {
      return;
    }

    // request remaining pages
    if (!cancelToken.isCancellationRequested() && !(docs.length < this.itemsPerPage)) {
      this.context.dispatch("fetchRest", { query: query, cancelToken: cancelToken });
    }
  }

  @Action
  async fetchRest(data: { query: PaginationParamsType; cancelToken: CancelToken }) {
    // fetch next page
    let docs: DocumentType[] = await this.context.dispatch("fetchNext", data.query);

    // check if all are supposed to be loaded
    if (!this.isLoadAll) {
      return;
    }

    // get rest while there is something to get and request is not interrupted
    while (!data.cancelToken.isCancellationRequested() && this.itemsPerPage && !(docs.length < this.itemsPerPage)) {
      docs = await this.context.dispatch("fetchNext", data.query);
    }
  }

  @Action
  async fetchNext(query: PaginationParamsType): Promise<DocumentType[]> {
    const documents = await this.context.dispatch("loadDocuments", {
      itemsPerPage: this.itemsPerPage,
      order: this.pageOrder,
      currentPage: this.currentPage,
      ...query,
      startId: this.lastId
    });

    // set meta information
    this.context.dispatch("setMeta", documents.meta);

    // set items
    this.context.dispatch("setToList", documents.data);

    return documents.data;
  }

  @Action
  emptyList() {
    this.context.dispatch("setPaginationList", []);
  }
}
